import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { buildBasicListRequestParams } from '@app/helpers/listQuery.helper';
import {
  InvestmentDataRequest,
  InvestmentResponse,
  ProjectBaseRequest,
} from '@app/models/admin.model';
import {
  MeetingListResponse,
  MeetingQuery,
  MeetingRequest,
  MeetingResponse,
} from '@app/models/meeting.model';
import { Project } from '@app/models/project.models';
import {
  ComplexPaginatedDataRequest,
  PaginatedData,
  ProjectDataRequest,
} from '@app/models/shared.models';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ProjectAdminService {
  private readonly API_URL = environment.apiUrl;

  constructor(private http: HttpClient) {}

  // ProjectAdmin
  getProjectIdeas(params: ProjectDataRequest): Observable<any> {
    const requestParams:
      | HttpParams
      | Record<
          string,
          string | number | boolean | ReadonlyArray<string | number | boolean>
        > = {};

    if (params.skip?.toString()) {
      requestParams['skip'] = params.skip?.toString();
    }
    if (params.take?.toString()) {
      requestParams['take'] = params.take?.toString();
    }
    if (params.sort) {
      requestParams['sort'] = JSON.stringify(params.sort);
    }
    if (params.filter) {
      requestParams['filter'] = params.filter;
    }
    if (params.currency?.length) {
      requestParams['currency'] = params.currency?.join('');
    }
    if (params.country?.length) {
      requestParams['country'] = params.country?.join('');
    }
    if (params.category?.length) {
      requestParams['category'] = params.category?.join('');
    }
    if (params.interest) {
      requestParams['interest'] = params.interest;
    }
    if (params.yield) {
      requestParams['yield'] = params.yield;
    }
    if (params.duration) {
      requestParams['duration'] = params.duration;
    }

    return this.http.get(`${this.API_URL}/Projects/Ideas`, {
      params: requestParams,
    });
  }

  getProjectsAdmin(
    params: ProjectDataRequest = {},
  ): Observable<PaginatedData<Project>> {
    const requestParams:
      | HttpParams
      | Record<
          string,
          string | number | boolean | ReadonlyArray<string | number | boolean>
        > = {};

    if (params.skip?.toString()) {
      requestParams['skip'] = params.skip.toString();
    }
    if (params.take?.toString()) {
      requestParams['take'] = params.take.toString();
    }
    if (params.sort) {
      requestParams['sort'] = JSON.stringify(params.sort);
    }
    if (params.filter) {
      requestParams['filter'] = params.filter;
    }
    if (params.currency?.length) {
      requestParams['currency'] = params.currency.join(',');
    }
    if (params.country?.length) {
      requestParams['country'] = params.country.join(',');
    }
    if (params.category?.length) {
      requestParams['category'] = params.category.join(',');
    }
    if (params.interest) {
      requestParams['interest'] = params.interest;
    }
    if (params.yield) {
      requestParams['yield'] = params.yield;
    }
    if (params.duration) {
      requestParams['duration'] = params.duration;
    }

    return this.http.get<PaginatedData<Project>>(
      `${this.API_URL}/ProjectsAdmin`,
      {
        params: requestParams,
      },
    );
  }

  getProjectAdminById(projectId: string): Observable<any> {
    return this.http.get(`${this.API_URL}/ProjectAdmin/${projectId}`);
  }

  getFilesForProject(projectId: string): Observable<any> {
    return this.http.get(`${this.API_URL}/ProjectAdmin/${projectId}/Files`);
  }

  updateProjectAdmin(
    projectId: string,
    body: ProjectBaseRequest
  ): Observable<void> {
    return this.http.put<void>(
      `${this.API_URL}/ProjectAdmin/${projectId}`,
      body
    );
  }

  // ProjectState
  updateProjectStateToStaffAnalyzeStarted(projectId: string): Observable<void> {
    return this.http.put<void>(
      `${this.API_URL}/ProjectState/${projectId}/StaffAnalyzeStarted`,
      {}
    );
  }
  updateProjectStateToStaffAccepted(projectId: string): Observable<void> {
    return this.http.put<void>(
      `${this.API_URL}/ProjectState/${projectId}/StaffAccepted`,
      {}
    );
  }
  updateProjectStateToDataSupplyNeeded(projectId: string): Observable<void> {
    return this.http.put<void>(
      `${this.API_URL}/ProjectState/${projectId}/DataSupplyNeeded`,
      {}
    );
  }
  updateProjectStateToPublishReady(projectId: string): Observable<void> {
    return this.http.put<void>(
      `${this.API_URL}/ProjectState/${projectId}/PublishReady`,
      {}
    );
  }
  updateProjectStateToPublishable(projectId: string): Observable<void> {
    return this.http.put<void>(
      `${this.API_URL}/ProjectState/${projectId}/Publishable`,
      {}
    );
  }
  updateProjectStateToRefuse(projectId: string): Observable<void> {
    return this.http.put<void>(
      `${this.API_URL}/ProjectState/${projectId}/Refuse`,
      {}
    );
  }
  updateProjectStateToPublished(projectId: string): Observable<void> {
    return this.http.put<void>(
      `${this.API_URL}/ProjectState/${projectId}/Published`,
      {}
    );
  }

  // Meetings
  getMeetingsAdmin(params: MeetingQuery = {}): Observable<MeetingListResponse> {
    let httpParams = new HttpParams();

    if (params.skip != null)
      httpParams = httpParams.set('skip', String(params.skip));
    if (params.take != null)
      httpParams = httpParams.set('take', String(params.take));
    if (params.sort?.length)
      httpParams = httpParams.set('sort', JSON.stringify(params.sort));
    if (params.filter) httpParams = httpParams.set('filter', params.filter);
    if (params.project) httpParams = httpParams.set('project', params.project);

    return this.http.get<MeetingListResponse>(`${this.API_URL}/MeetingsAdmin`, {
      params: httpParams,
    });
  }

  // returns only active or ongoing meetings for projects
  getMeetings(params: MeetingQuery = {}): Observable<MeetingListResponse> {
    let httpParams = new HttpParams();

    if (params.skip != null)
      httpParams = httpParams.set('skip', String(params.skip));
    if (params.take != null)
      httpParams = httpParams.set('take', String(params.take));
    if (params.sort?.length)
      httpParams = httpParams.set('sort', JSON.stringify(params.sort));
    if (params.filter) httpParams = httpParams.set('filter', params.filter);
    if (params.project) httpParams = httpParams.set('project', params.project);
    return this.http.get<MeetingListResponse>(`${this.API_URL}/Meetings`, {
      params: httpParams,
    });
  }
  getMeetingById(meetingId: string): Observable<MeetingResponse> {
    return this.http.get<MeetingResponse>(
      `${this.API_URL}/Meeting/${meetingId}`
    );
  }
  createMeeting(body: MeetingRequest): Observable<MeetingResponse> {
    return this.http.post<MeetingResponse>(`${this.API_URL}/Meeting`, body);
  }
  cancelMeetingByContact(meetingId: string): Observable<void> {
    return this.http.put<void>(
      `${this.API_URL}/Meeting/${meetingId}/CancelByContact`,
      {}
    );
  }
  acceptMeetingByCustomer(meetingId: string, dateId: string): Observable<void> {
    return this.http.put<void>(
      `${this.API_URL}/Meeting/${meetingId}/Accept/${dateId}`,
      {}
    );
  }
  cancelMeetingByCustomer(meetingId: string): Observable<void> {
    return this.http.put<void>(
      `${this.API_URL}/Meeting/${meetingId}/CancelByCustomer`,
      {}
    );
  }
  setMeetingToDone(meetingId: string): Observable<void> {
    return this.http.put<void>(`${this.API_URL}/Meeting/${meetingId}/Done`, {});
  }

  // ProjectInvestmentAdmin
  getInvestments(
    params: InvestmentDataRequest
  ): Observable<PaginatedData<InvestmentResponse>> {
    let httpParams = buildBasicListRequestParams(params);

    if (params.status?.length) {
      const values = params.status.map((s) => String(s)).join(',');
      httpParams = httpParams.set('Status', values);
    }
    if (params.project?.trim()) {
      httpParams = httpParams.set('Project', params.project.trim());
    }
    if (params.user?.trim()) {
      httpParams = httpParams.set('User', params.user.trim());
    }

    return this.http.get<PaginatedData<InvestmentResponse>>(
      `${this.API_URL}/InvestmentsAdmin`,
      { params: httpParams }
    );
  }

  getInvestmentsByProjectId(
    projectId: string,
    params: ComplexPaginatedDataRequest = {}
  ): Observable<PaginatedData<InvestmentResponse>> {
    const httpParams: HttpParams = buildBasicListRequestParams(params);

    return this.http.get<PaginatedData<InvestmentResponse>>(
      `${this.API_URL}/InvestmentAdmin/Project/${projectId}`,
      { params: httpParams }
    );
  }

  getInvestmentById(investmentId: string): Observable<InvestmentResponse> {
    return this.http.get<InvestmentResponse>(
      `${this.API_URL}/InvestmentAdmin/${investmentId}`
    );
  }
  acceptInvestment(investmentId: string): Observable<void> {
    return this.http.put<void>(
      `${this.API_URL}/InvestmentAdmin/${investmentId}/Accept`,
      {}
    );
  }
  markInvestmentAsCredited(investmentId: string): Observable<void> {
    return this.http.put<void>(
      `${this.API_URL}/InvestmentAdmin/${investmentId}/Credited`,
      {}
    );
  }
  refuseInvestment(investmentId: string): Observable<void> {
    return this.http.put<void>(
      `${this.API_URL}/InvestmentAdmin/${investmentId}/Refuse`,
      {}
    );
  }
}
